import React, { useState, useEffect, useRef } from "react";
import { LearnWorkbenchShell } from "../components/layout/LearnWorkbenchShell";
import { learnSurface } from "../lib/learnSurfaceClasses";
import { PracticeSurface } from "../components/practice/PracticeSurface";
import { VexFlowRenderer } from "../components/VexFlowRenderer";
import { useAudioEngine } from "../hooks/useAudioEngine";
import { TaskCreatorButton } from "../components/TaskCreatorButton";
import { parseMusicXml, findNotePositions, type NoteName, type NeckPosition, type MusicXmlNote } from "../lib/guitarEngine";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { trpc } from "../providers/trpc";

export const MelodyPracticePage: React.FC = () => {
  const [selectedSongId, setSelectedSongId] = useState<number | null>(null);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [correctNotes, setCorrectNotes] = useState(0);
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeNeckPosition, setActiveNeckPosition] = useState<{ string: number; fret: number } | null>(null);

  const songsQuery = trpc.song.list.useQuery();
  const selectedSong = songsQuery.data?.find((s) => s.id === selectedSongId);

  const notes: MusicXmlNote[] = selectedSong ? parseMusicXml(selectedSong.musicXml) : [];
  const currentNote = notes[currentNoteIndex];

  const currentNoteRef = useRef(currentNote);
  const currentNoteIndexRef = useRef(currentNoteIndex);
  const notesRef = useRef(notes);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => { currentNoteRef.current = currentNote; }, [currentNote]);
  useEffect(() => { currentNoteIndexRef.current = currentNoteIndex; }, [currentNoteIndex]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

  const {
    state: engineState,
    init,
    detectNote,
    prepareForNextNote,
    waitForSilence,
    stopRecording,
  } = useAudioEngine();

  useEffect(() => {
    if (!engineState.isReady && !engineState.isLoading) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentNote) {
      const positions = findNotePositions(currentNote.step as NoteName, 15);
      if (positions.length > 0) {
        const pos = positions.find((p) => p.fret <= 5) || positions[0];
        setActiveNeckPosition({ string: pos.string, fret: pos.fret });
      }
    } else {
      setActiveNeckPosition(null);
    }
  }, [currentNote]);

  useEffect(() => {
    if (!isPlaying) return;
    let cancelled = false;

    const loop = async () => {
      while (!cancelled && isPlayingRef.current) {
        const cn = currentNoteRef.current;
        if (!cn) {
          await new Promise((r) => setTimeout(r, 50));
          continue;
        }

        const expectedIndex = currentNoteIndexRef.current;
        const expectedMidi = cn.midi;

        const detected = await detectNote(3500, {
          preferMidi: expectedMidi,
          acceptOctaveErrors: true,
          holdTimeMs: 180,
          minConfidence: 0.52,
          requireSilenceBeforeMs: 130,
          maxCents: 45,
        });
        if (cancelled || !isPlayingRef.current) break;

        if (
          detected &&
          currentNoteIndexRef.current === expectedIndex &&
          currentNoteRef.current?.midi === expectedMidi
        ) {
          setCorrectNotes((prev) => prev + 1);
          setCurrentNoteIndex((prev) => {
            if (prev !== expectedIndex) return prev;
            const next = prev + 1;
            if (next >= notesRef.current.length) {
              setIsPlaying(false);
              return prev;
            }
            return next;
          });
          prepareForNextNote();
          await waitForSilence({ minMs: 130, timeoutMs: 550 });
        }
        setTotalAttempts((prev) => prev + 1);
      }
    };

    void loop();
    return () => {
      cancelled = true;
      stopRecording();
    };
  }, [isPlaying, detectNote, prepareForNextNote, waitForSilence, stopRecording]);

  const handleStart = () => {
    setIsPlaying(true);
    setCurrentNoteIndex(0);
    setCorrectNotes(0);
    setTotalAttempts(0);
  };

  const handleStop = () => {
    setIsPlaying(false);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentNoteIndex(0);
    setCorrectNotes(0);
    setTotalAttempts(0);
    setActiveNeckPosition(null);
  };

  const highlightedPositions: NeckPosition[] = currentNote
    ? findNotePositions(currentNote.step as NoteName, 15).map((p) => ({ ...p, highlight: "active" as const }))
    : [];

  const accuracy = totalAttempts > 0 ? Math.round((correctNotes / totalAttempts) * 100) : 0;

  // Sidebar
  const sidebar = (
    <div className="space-y-5" style={{ animation: "panelSlideIn 0.3s ease-out" }}>
      <div>
        <label className={learnSurface.labelMb2}>Canción</label>
        <Select value={selectedSongId ? String(selectedSongId) : ""} onValueChange={(v) => setSelectedSongId(Number(v))}>
          <SelectTrigger
            className={`w-full h-9 font-sans border-white/12 bg-white/[0.07] text-slate-100 shadow-none [&_svg]:text-slate-400 data-[placeholder]:text-slate-500 focus-visible:ring-[var(--bordona-accent-border)]/40`}
          >
            <SelectValue placeholder="Seleccionar..." />
          </SelectTrigger>
          <SelectContent className="border-white/12 bg-slate-900/95 text-slate-100 backdrop-blur-md">
            {songsQuery.data?.map((song) => (
              <SelectItem
                key={song.id}
                value={String(song.id)}
                className="font-sans text-slate-100 focus:bg-white/10 focus:text-slate-50"
              >
                {song.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={`pt-3 border-t ${learnSurface.divider}`}>
        <label className={learnSurface.labelMb2}>Controles</label>
        <div className="flex gap-2">
          {!isPlaying ? (
            <button
              onClick={handleStart}
              disabled={!selectedSongId}
              className={`flex-1 py-2.5 rounded-md text-sm font-sans font-medium transition-opacity disabled:opacity-50 ${learnSurface.btnPrimary}`}
            >
              ▶ Iniciar
            </button>
          ) : (
            <button
              onClick={handleStop}
              className="flex-1 py-2.5 rounded-md text-sm font-sans font-medium border border-[var(--bordona-accent)] text-[var(--bordona-accent)] hover:bg-[var(--bordona-accent-soft)]/30"
            >
              ⏹ Detener
            </button>
          )}
          <button
            onClick={handleReset}
            className={`px-3 py-2.5 rounded-md text-sm font border border-white/12 text-slate-300 hover:bg-white/10`}
          >
            ↻
          </button>
        </div>
      </div>

      {isPlaying && (
        <div className={`space-y-2 pt-3 border-t ${learnSurface.divider}`}>
          <div className="flex justify-between text-sm font-sans">
            <span className={learnSurface.textMuted}>Nota</span>
            <span className={`${learnSurface.textStrong} font-medium`}>
              {currentNote ? `${currentNote.step}${currentNote.alter ? (currentNote.alter > 0 ? "#" : "b") : ""}` : "—"}
            </span>
          </div>
          <div className="flex justify-between text-sm font-sans">
            <span className={learnSurface.textMuted}>Progreso</span>
            <span className={`${learnSurface.textStrong} font-medium`}>
              {currentNoteIndex + 1} / {notes.length}
            </span>
          </div>
          <div className="flex justify-between text-sm font-sans">
            <span className={learnSurface.textMuted}>Aciertos</span>
            <span className="text-emerald-400 font-medium">{correctNotes}</span>
          </div>
          <div className="flex justify-between text-sm font-sans">
            <span className={learnSurface.textMuted}>Precisión</span>
            <span className="text-amber-300 font-medium">{accuracy}%</span>
          </div>
        </div>
      )}

      {engineState.isAnalyzing && (
        <div className={`pt-3 border-t ${learnSurface.divider}`}>
          <label className={learnSurface.labelMb2}>Micrófono</label>
          <div className="text-sm font-sans font-bold text-[var(--bordona-accent)]">Escuchando...</div>
        </div>
      )}
    </div>
  );

  // Inspector
  const inspector = selectedSong ? (
    <div className="space-y-4">
      <div>
        <h4 className={`${learnSurface.label} mb-3`}>Partitura</h4>
        <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#f8f9fa] p-2 shadow-inner">
          <VexFlowRenderer musicXml={selectedSong.musicXml} currentNoteIndex={currentNoteIndex} width={280} height={160} />
        </div>
      </div>
      <div className={`pt-3 border-t ${learnSurface.divider}`}>
        <h4 className={learnSurface.labelMb2}>Notas</h4>
        <div className="flex flex-wrap gap-1">
          {notes.map((note, idx) => (
            <span
              key={idx}
              className={`px-1.5 py-0.5 rounded text-[10px] font-sans border ${
                idx === currentNoteIndex
                  ? "bg-[var(--bordona-accent)] text-white border-[var(--bordona-accent-border)]"
                  : "bg-white/[0.06] text-slate-300 border-white/10"
              }`}
            >
              {note.step}{note.alter ? (note.alter > 0 ? "#" : "b") : ""}{note.octave}
            </span>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  // Bottom
  const bottom = (
    <div className="flex items-center gap-8">
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-[var(--bordona-accent)] inline-block ring-1 ring-[var(--bordona-accent-border)]/50" />
        <span className={`text-xs font-sans ${learnSurface.textBody}`}>Nota objetivo</span>
      </div>
      <TaskCreatorButton
        type="melody"
        title={selectedSong ? selectedSong.title : "Melodía"}
        config={{ melodyId: selectedSong?.id || "custom", melodyTitle: selectedSong?.title || "Melodía personalizada" }}
      />
      <div className="flex-1" />
      <p className={`text-xs font-sans ${learnSurface.textMuted}`}>
        Toca la nota resaltada. El micrófono validará automáticamente.
      </p>
    </div>
  );

  return (
    <LearnWorkbenchShell
      sidebar={sidebar}
      inspector={inspector || undefined}
      bottom={bottom}
      title={selectedSong ? selectedSong.title : "Melodías Sencillas"}
      activeModule="melodies"
    >
      <div className="flex-1 flex items-center justify-center p-2 min-h-0">
        <div className="w-full h-full max-w-4xl">
          <PracticeSurface
            highlightedPositions={highlightedPositions}
            activeNote={activeNeckPosition}
            showNoteNames={true}
            showDegrees={false}
            interactive={false}
            stringOrder="physical"
            visualStyle="lutherie"
          />
        </div>
      </div>
    </LearnWorkbenchShell>
  );
};

export default MelodyPracticePage;
